var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
/**
 * EventLegend Component
 * Dropdown button showing color-coded event type legend.
 * Replicates the GRM "EVENT LEGEND" dropdown from calendardashboardpartial.html.
 */
import React, { memo, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { styles } from './EventLegend.styles';
/** Default legend items matching the yearly calendar event colors */
export const DEFAULT_LEGEND_ITEMS = [
    { label: 'Project', color: '#008272', textColor: '#fff' },
    { label: 'Non-Project', color: '#0b400b', textColor: '#fff' },
    { label: 'Public Holiday', color: '#5C2D91', textColor: '#fff' },
    { label: 'ROSS', color: '#A42E00', textColor: '#fff' },
    { label: 'Outlook (Busy/OOF)', color: '#4098FC', textColor: '#fff' },
    { label: 'Outlook (Other)', color: '#a9a9a9', textColor: '#fff' },
];
/**
 * EventLegend - Dropdown toggle showing calendar event type colors
 */
export const EventLegend = memo(({ items = DEFAULT_LEGEND_ITEMS, buttonLabel = 'EVENT LEGEND', }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const toggleButtonRef = useRef(null);
    const dropdownRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, minWidth: 200 });
    const updateDropdownPosition = useCallback(() => {
        const button = toggleButtonRef.current;
        if (!button) {
            return;
        }
        const rect = button.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const desiredWidth = Math.max(rect.width, 200);
        const maxLeft = Math.max(8, viewportWidth - desiredWidth - 8);
        const left = Math.min(rect.left, maxLeft);
        setDropdownPosition({
            top: rect.bottom,
            left,
            minWidth: desiredWidth,
        });
    }, []);
    // Toggle dropdown
    const handleToggle = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);
    // Keyboard: Enter/Space to toggle, Escape to close
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(prev => !prev);
        }
        else if (e.key === 'Escape' && isOpen) {
            setIsOpen(false);
        }
    }, [isOpen]);
    // Close on outside click
    useEffect(() => {
        if (!isOpen)
            return;
        const handleDocumentClick = (e) => {
            var _a, _b;
            const target = e.target;
            const clickedToggle = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(target);
            const clickedDropdown = (_b = dropdownRef.current) === null || _b === void 0 ? void 0 : _b.contains(target);
            if (!clickedToggle && !clickedDropdown) {
                setIsOpen(false);
            }
        };
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setIsOpen(false);
            }
        };
        updateDropdownPosition();
        document.addEventListener('click', handleDocumentClick);
        document.addEventListener('keydown', handleEscape);
        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition, true);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
            document.removeEventListener('keydown', handleEscape);
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
        };
    }, [isOpen, updateDropdownPosition]);
    return (React.createElement("div", { ref: containerRef, style: styles.container },
        React.createElement("button", { ref: toggleButtonRef, style: Object.assign(Object.assign({}, styles.toggleButton), (isOpen ? styles.toggleButtonOpen : {})), onClick: handleToggle, onKeyDown: handleKeyDown, "aria-expanded": isOpen, "aria-label": buttonLabel, "aria-haspopup": "true", type: "button" },
            React.createElement("span", { style: styles.icon, "aria-hidden": "true" }, "\u2630"),
            React.createElement("span", { style: styles.label }, buttonLabel)),
        isOpen && typeof document !== 'undefined' && createPortal(React.createElement("div", { ref: dropdownRef, style: Object.assign(Object.assign({}, styles.dropdown), { top: dropdownPosition.top, left: dropdownPosition.left, minWidth: dropdownPosition.minWidth }), role: "list", "aria-label": "Event type legend", tabIndex: 0 },
            React.createElement("ul", { style: styles.list }, items.map((item, idx) => (React.createElement("li", { key: idx, style: styles.listItem, role: "listitem" },
                React.createElement("span", { style: Object.assign(Object.assign({}, styles.colorBox), { backgroundColor: item.color, color: item.textColor || '#fff' }), "aria-hidden": "true" }, item.label.charAt(0)),
                React.createElement("span", { style: styles.legendText }, item.label)))))), document.body)));
});
EventLegend.displayName = 'EventLegend';
export default EventLegend;
//# sourceMappingURL=EventLegend.js.map